import { useSnackbar } from 'notistack';
import { useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, MenuItem, styled } from '@mui/material';
// hooks
import { useNavigate } from 'react-router';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import CustomModal from '../../../components/CustomModal';
import ChangePassword from '../../../pages/auth/ChangePassword';
import { useDispatch, useSelector } from '../../../redux/store';
import { closeChangePwd, openChangePwd } from '../../../redux/slices/authPages';
import useLocales from '../../../hooks/useLocales';
import { fNumberDecimal } from '../../../utils/formatNumber';
import Iconify from '../../../components/Iconify';
import { openConfirm } from '../../../redux/slices/confirm';

// ----------------------------------------------------------------------

const MenuText = styled(Typography)(({ theme }) => ({
  color: '#6E2100',
  fontWeight: 700,
}));

export default function AccountPopover() {
  const navigate = useNavigate();

  const { userProfile, logout } = useAuth();

  const { translate } = useLocales();

  const { isOpenChangePwd } = useSelector((x) => x.authPages);

  const { memberBalance } = useSelector((x) => x.lookup);

  const dispatch = useDispatch();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ml: 0.5,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        {/* <Image src={require('../../../assets/profile.png')} sx={{ width: 30, height: 30, cursor: 'pointer' }} /> */}
        <Iconify icon={'mingcute:user-4-fill'} sx={{ width: 30, height: 30, cursor: 'pointer', color: '#f5b007' }} />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
          background: 'linear-gradient(to bottom, #CDCDCD 0%, #FFFFFF 100%)',
          color: 'rgb(253, 169, 45)',
        }}
        disabledArrow={true}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <MenuText variant="subtitle2" noWrap>
            {userProfile?.username}
          </MenuText>
          <MenuText variant="subtitle2" sx={{ color: 'rgba(0, 0, 0, 0.5)' }} noWrap>
            {userProfile?.UserProfile?.no_hp}
          </MenuText>

          <MenuText variant="subtitle2" noWrap sx={{ color: '#f5b007' }}>
            {`${memberBalance?.currencyCode} ${
              memberBalance?.displayBalance ? fNumberDecimal(memberBalance?.displayBalance) : 0
            }`}
          </MenuText>
        </Box>

        <Divider sx={{ borderStyle: 'dashed', borderColor: 'white' }} />

        <MenuItem
          onClick={() => {
            navigate('/user/account');
            handleClose();
          }}
          sx={{ m: 1, fontWeight: 'bold' }}
        >
          <MenuText>{translate('account')}</MenuText>
        </MenuItem>

        {/* <MenuItem
          onClick={() => {
            navigate('/user/achievements');
            handleClose();
          }}
          sx={{ m: 1, fontWeight: 'bold' }}
        >
          <MenuText>{translate('achievements')}</MenuText>
        </MenuItem> */}

        <MenuItem
          onClick={() => {
            navigate('/user/banks');
            handleClose();
          }}
          sx={{ m: 1, fontWeight: 'bold' }}
        >
          <MenuText>{translate('bank_account')}</MenuText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate('/referral');
            handleClose();
          }}
          sx={{ m: 1, fontWeight: 'bold' }}
        >
          <MenuText>{translate('referral')}</MenuText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            dispatch(openChangePwd());
            handleClose();
          }}
          sx={{ m: 1, fontWeight: 'bold' }}
        >
          <MenuText>{translate('change_password')}</MenuText>
        </MenuItem>

        <MenuItem
          onClick={() =>
            openConfirm({
              title: translate('logout_confirmation'),
              subTitle: translate('are_you_sure_to_logout'),
              onConfirm: handleLogout,
            })
          }
          sx={{ m: 1, fontWeight: 'bold' }}
        >
          <MenuText>{translate('logout')}</MenuText>
        </MenuItem>
      </MenuPopover>

      <CustomModal
        maxWidth={'sm'}
        open={isOpenChangePwd}
        onClose={() => dispatch(closeChangePwd())}
        children={<ChangePassword />}
      />
    </>
  );
}
